import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const sectionState = new WeakMap();
const previewTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const garmentColors = {
  blue: '#3574b6', beige: '#d5c4a7', green: '#417849', gray: '#929292', grey: '#929292',
  black: '#252525', white: '#f5f5f0', pink: '#e6a4b5', navy: '#23344f', yellow: '#ebcb4b',
  red: '#bd3535', purple: '#79508e', orange: '#dd7836', 'grey marle': '#a0a0a0', 'black marle': '#474747',
};

/**
 * @typedef {object} PreviewRefs
 * @property {HTMLScriptElement} productData
 * @property {HTMLElement} stage
 * @property {HTMLElement} artworkBox
 * @property {HTMLImageElement} artwork
 * @property {HTMLElement} status
 * @property {HTMLElement} selection
 * @property {HTMLElement} mockupNote
 * @property {HTMLElement} colorNote
 * @property {HTMLElement | undefined} printArea
 * @property {HTMLButtonElement[]} viewButtons
 * @property {HTMLTextAreaElement | undefined} messageInput
 * @property {HTMLElement | undefined} messageBox
 * @property {HTMLElement | undefined} messageText
 * @property {HTMLElement | undefined} messageField
 * @property {HTMLElement | undefined} messageCount
 * @extends Component<PreviewRefs>
 */
class MerchHousePreview extends Component {
  requiredRefs = ['productData', 'stage', 'artworkBox', 'artwork', 'status', 'selection'];
  #controller;
  #observer;
  #section;
  #state = {
    file: null, view: 'front', input: null, propertyField: null, propertyValue: '',
    savedUrl: null, savedName: '', savedType: '', hasSavedProperty: false,
    ignoredPropertyUrl: null, ignoredSavedUrl: null, removedUrl: null, nativePending: false, needsReselect: false,
    message: '', messageInitialized: false, messageProductId: null,
  };
  #objectUrl;
  #loadedFile;
  #loadedUrl;
  #previewSource;
  #imageError = false;
  #scheduled = false;
  #selectionOverride;
  #generation = 0;
  #lastPlacement;
  #messageControl;
  #messageControlsBound = new WeakSet();
  #messageResizeObserver;
  #observedMessageBox;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#messageControlsBound = new WeakSet();
    this.#section = this.closest('.shopify-section');
    if (!this.#section) return;

    let states = sectionState.get(this.#section);
    if (!states) {
      states = new Map();
      sectionState.set(this.#section, states);
    }
    this.#state = states.get(this.id) ?? this.#state;
    states.set(this.id, this.#state);

    const { signal } = this.#controller;
    this.#section.addEventListener('change', this.#handleChange, { capture: true, signal });
    this.#section.addEventListener('input', this.#handleChange, { capture: true, signal });
    this.#section.addEventListener('upload:added', this.#handleUploadAdded, { signal });
    this.#section.addEventListener('upload:removed', this.#handleUploadRemoved, { signal });
    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener('error', this.#handleImageError, { capture: true, signal });
    this.#observer?.disconnect();
    this.#messageResizeObserver?.disconnect();
    this.#observedMessageBox = null;
    this.#observer = new MutationObserver((records) => {
      if (records.some((record) => !this.contains(record.target))) this.#scheduleRender();
    });
    this.#observer.observe(this.#section, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled', 'name', 'form'] });
    if (!this.#state.file) {
      const selectedInputs = Array.from(this.#section.querySelectorAll('input[type="file"]'))
        .filter((input) => this.#matchesUpload(input) && input.files?.length);
      if (selectedInputs.length === 1) this.#rememberInput(selectedInputs[0]);
    }
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#messageResizeObserver?.disconnect();
    this.#observedMessageBox = null;
    this.#generation += 1;
    this.#selectionOverride = null;
    this.#revokeUrl();
    this.#loadedFile = undefined;
    this.#loadedUrl = undefined;
    this.#previewSource = null;
  }

  /** @param {MouseEvent} event */
  handleView(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-view]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    this.#state.view = button.dataset.view === 'back' ? 'back' : 'front';
    this.#render();
  }

  #handleChange = (event) => {
    const path = event.composedPath();
    const messageControls = this.#messageControls(this.#configuration());
    if (path.some((node) => messageControls.includes(node))) return;
    const input = path.find((node) => node instanceof HTMLInputElement && node.type === 'file');
    if (input && this.#matchesUpload(input, path)) {
      this.#rememberInput(input);
      this.#imageError = false;
    }
    this.#scheduleRender();
  };

  #handleProductSelect = async (event) => {
    const productId = event.product?.id ?? (event.target instanceof Element ? event.target.closest('variant-picker')?.dataset.productId : null);
    if (String(productId ?? '') !== this.dataset.productId) return;
    const generation = ++this.#generation;
    if (Array.isArray(event.selectedOptions)) {
      this.#selectionOverride = new Map(event.selectedOptions.map(({ name, value }) => [String(name).trim().toLowerCase(), String(value)]));
      this.#render();
    }
    try {
      if (event.promise) await event.promise;
    } catch {
      return;
    }
    if (this.isConnected && generation === this.#generation) {
      this.#selectionOverride = null;
      this.#scheduleRender();
    }
  };

  #handleImageError = (event) => {
    if (event.target !== this.refs.artwork || !this.#previewSource) return;
    this.#imageError = true;
    this.#render();
  };

  #matchesUpload(input, path = []) {
    if (!this.#ownsUploadInput(input, path)) return false;
    const expected = this.#propertyName(this.dataset.uploadProperty ?? 'upload');
    if (input.name && this.#propertyName(input.name) === expected) return true;

    const appBlock = path.map((node) => node instanceof Element ? node.closest('.shopify-app-block') : null).find(Boolean) ?? input.closest('.shopify-app-block');
    const knownBlocks = (this.dataset.uploadBlockIds ?? '').split(',').filter(Boolean);
    const knownUploader = appBlock && knownBlocks.some((blockId) => appBlock.id.includes(blockId));
    const marker = path.find((node) => node instanceof Element && node.getAttribute('data-upload-lift') !== null) ?? input.closest('[data-upload-lift]');
    const correctProduct = marker?.getAttribute('data-upload-lift') === this.dataset.productId;
    if (appBlock && (knownUploader || correctProduct)) {
      const inputs = Array.from(appBlock.querySelectorAll('input[type="file"]'));
      const mapped = this.#uploadPropertyFields().length > 0;
      if (mapped && inputs.length === 1 && inputs[0] === input) return true;
    }

    const fileInputs = Array.from(this.#section.querySelectorAll('input[type="file"]'));
    return Boolean((knownUploader || correctProduct) && fileInputs.length === 1 && fileInputs[0] === input);
  }

  #rememberInput(input) {
    const attachment = this.#readAttachment();
    const selectedFile = input.files?.[0] ?? null;
    if (selectedFile && selectedFile !== this.#state.file) {
      this.#state.file = selectedFile;
      this.#state.ignoredSavedUrl = this.#state.savedUrl;
      this.#state.savedUrl = null;
      this.#state.savedName = '';
      this.#state.savedType = '';
      this.#state.hasSavedProperty = false;
      this.#state.ignoredPropertyUrl = attachment.url;
      this.#state.removedUrl = null;
      this.#state.nativePending = true;
    } else if (!selectedFile && !this.#state.savedUrl && !attachment.url) {
      this.#state.file = null;
      this.#state.nativePending = false;
    }
    this.#state.input = input;
    this.#state.needsReselect = false;
    this.#state.propertyField = attachment.field;
    this.#state.propertyValue = attachment.url ?? '';
  }

  #productForm() {
    const formId = this.dataset.formId;
    const form = formId ? this.ownerDocument.getElementById(formId) : this.#section.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement) || !this.#section.contains(form)) return null;
    const owner = form.closest('product-form-component');
    if (owner?.dataset.productId && owner.dataset.productId !== this.dataset.productId) return null;
    return form;
  }

  #ownsUploadInput(input, path = []) {
    const form = this.#productForm();
    if (!form) return false;
    if (input.form) return input.form === form;
    if (input.getAttribute('form')) return input.getAttribute('form') === form.id;
    const marker = path.find((node) => node instanceof Element && node.getAttribute('data-upload-lift') !== null) ?? input.closest('[data-upload-lift]');
    if (marker) return marker.getAttribute('data-upload-lift') === this.dataset.productId;
    const appBlock = path.map((node) => node instanceof Element ? node.closest('.shopify-app-block') : null).find(Boolean) ?? input.closest('.shopify-app-block');
    return Boolean(appBlock && (this.dataset.uploadBlockIds ?? '').split(',').filter(Boolean).some((id) => appBlock.id.includes(id)));
  }

  #uploadPropertyFields() {
    const form = this.#productForm();
    if (!form) return [];
    const expected = this.#propertyName(this.dataset.uploadProperty ?? 'upload');
    return Array.from(form.elements).filter((field) =>
      field instanceof HTMLInputElement && field.form === form && !field.disabled &&
      field.type !== 'file' && /^properties\[[^\]]+\]$/.test(field.name) && this.#propertyName(field.name) === expected
    );
  }

  #readAttachment() {
    const fields = this.#uploadPropertyFields();
    const attachments = fields.map((field) => ({ field, url: this.#httpsUrl(field.value) })).filter(({ url }) => url);
    const urls = new Set(attachments.map(({ url }) => url));
    if (urls.size === 1) return attachments[0];
    return { field: fields.length === 1 ? fields[0] : null, url: null };
  }

  #httpsUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value.trim());
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  }

  #ownsUploadEvent(event, url) {
    const form = this.#productForm();
    if (!form) return false;
    const path = event.composedPath();
    const source = path.find((node) => node instanceof Element);
    if (!source || (!path.includes(this.#section) && !this.#section.contains(source))) return false;
    const sourceForm = path.find((node) => node instanceof HTMLFormElement) ?? source.closest('form');
    if (sourceForm && sourceForm !== form) return false;
    const marker = path.find((node) => node instanceof Element && node.getAttribute('data-upload-lift') !== null) ?? source.closest('[data-upload-lift]');
    if (marker) return marker.getAttribute('data-upload-lift') === this.dataset.productId;
    const appBlock = path.map((node) => node instanceof Element ? node.closest('.shopify-app-block') : null).find(Boolean) ?? source.closest('.shopify-app-block');
    if (appBlock && (this.dataset.uploadBlockIds ?? '').split(',').filter(Boolean).some((id) => appBlock.id.includes(id))) return true;
    if (sourceForm !== form) return false;
    if (this.#readAttachment().url === url) return true;
    const uploaders = Array.from(form.querySelectorAll('file-upload, .file-upload'));
    return uploaders.length === 1 && uploaders[0].contains(source) && this.#uploadPropertyFields().length === 1;
  }

  #handleUploadAdded = (event) => {
    const upload = event.detail;
    if (!upload || upload.meta?.variant === 'thumbnail') return;
    const url = this.#httpsUrl(upload.url);
    if (!url || !this.#ownsUploadEvent(event, url)) return;
    if (url === this.#state.ignoredPropertyUrl || url === this.#state.ignoredSavedUrl) return;
    const attachment = this.#readAttachment();
    this.#state.ignoredPropertyUrl = attachment.url !== url ? attachment.url : null;
    this.#state.removedUrl = null;
    this.#saveAttachment(url);
    this.#state.hasSavedProperty = attachment.url === url;
    this.#render();
  };

  #handleUploadRemoved = (event) => {
    const upload = event.detail;
    if (!upload || upload.meta?.variant === 'thumbnail') return;
    const url = this.#httpsUrl(upload.url);
    if (!url || !this.#ownsUploadEvent(event, url)) return;
    if (this.#state.nativePending && (url === this.#state.ignoredPropertyUrl || url === this.#state.ignoredSavedUrl)) return;
    const currentUrl = this.#state.savedUrl ?? this.#readAttachment().url;
    if (url !== currentUrl) return;
    this.#clearAttachment(url);
    this.#render();
  };

  #saveAttachment(url) {
    const nativeMatches = this.#state.nativePending || this.#state.savedUrl === url;
    if (!nativeMatches) this.#state.file = null;
    this.#state.savedName = this.#state.file?.name ?? (this.#state.savedUrl === url ? this.#state.savedName : '');
    this.#state.savedType = this.#state.file?.type ?? (this.#state.savedUrl === url ? this.#state.savedType : '');
    this.#state.savedUrl = url;
    this.#state.nativePending = false;
    this.#state.needsReselect = false;
  }

  #clearAttachment(removedUrl = null) {
    this.#state.file = null;
    this.#state.savedUrl = null;
    this.#state.savedName = '';
    this.#state.savedType = '';
    this.#state.hasSavedProperty = false;
    this.#state.propertyField = null;
    this.#state.propertyValue = '';
    this.#state.input = null;
    this.#state.ignoredPropertyUrl = null;
    this.#state.ignoredSavedUrl = null;
    this.#state.removedUrl = removedUrl;
    this.#state.nativePending = false;
    this.#state.needsReselect = true;
  }

  #reconcileUploader() {
    const attachment = this.#readAttachment();
    const url = attachment.url;
    if (url && url !== this.#state.removedUrl && url !== this.#state.ignoredPropertyUrl) {
      this.#saveAttachment(url);
      this.#state.propertyField = attachment.field;
      this.#state.propertyValue = url;
      this.#state.hasSavedProperty = true;
    } else if (this.#state.hasSavedProperty && !url && !this.#state.nativePending) {
      this.#clearAttachment(this.#state.savedUrl);
    } else if (!this.#state.savedUrl && this.#state.input && !this.#state.input.isConnected) {
      this.#clearAttachment();
    }
  }

  #previewable(file, remoteUrl) {
    const type = (remoteUrl ? this.#state.savedType : file?.type ?? '').toLowerCase();
    const name = remoteUrl ? this.#state.savedName : file?.name ?? '';
    if (previewTypes.has(type) || /\.(png|jpe?g|webp|gif)$/i.test(name)) return true;
    let pathname = '';
    if (remoteUrl) pathname = new URL(remoteUrl).pathname;
    if (/\.(pdf|ai|eps|psd|svg|tiff?|zip)$/i.test(name || pathname)) return false;
    if (type && type !== 'application/octet-stream') return false;
    return Boolean(remoteUrl);
  }

  #propertyName(name) {
    return name.trim().replace(/^properties\[([^\]]+)\]$/, '$1').toLowerCase();
  }

  #scheduleRender() {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      if (this.isConnected) this.#render();
    });
  }

  #configuration() {
    try {
      const data = JSON.parse(this.refs.productData.textContent ?? '{}');
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  }

  #selectedOptions() {
    const data = this.#configuration();
    const names = Array.isArray(data.optionNames) ? data.optionNames : [];
    const values = Array.isArray(data.selectedOptions) ? data.selectedOptions : [];
    const options = new Map(names.map((name, index) => [String(name).trim().toLowerCase(), String(values[index] ?? '')]));
    const pickers = Array.from(this.#section.querySelectorAll('variant-picker'));
    const picker = pickers.find((element) => element.dataset.productId === this.dataset.productId);

    if (picker) {
      for (const input of picker.querySelectorAll('input:checked[data-option-name]')) {
        options.set(input.dataset.optionName.trim().toLowerCase(), input.value);
      }
      for (const select of picker.querySelectorAll('select')) {
        const option = select.selectedOptions[0];
        if (option?.dataset.optionName) options.set(option.dataset.optionName.trim().toLowerCase(), option.value);
      }
    }
    for (const [name, value] of this.#selectionOverride ?? []) options.set(name, value);
    return options;
  }

  #messageSelected(config, options) {
    const optionName = String(config.messageOptionName ?? '').trim().toLowerCase();
    const value = String(options.get(optionName) ?? '').trim().toLowerCase();
    return Boolean(optionName && Array.isArray(config.messageValues) && config.messageValues.some((entry) =>
      typeof entry === 'string' && entry.trim().toLowerCase() === value
    ));
  }

  #messageControls(config) {
    const form = this.#productForm();
    const propertyName = String(config.messagePropertyName ?? '').trim();
    if (!propertyName) return [];
    const expected = this.#propertyName(propertyName);
    const controls = form ? Array.from(form.elements) : [];
    const owned = this.refs.messageInput;
    // The preview precedes the purchase form. Keep its own field usable while
    // that form, or a replacement form, establishes its native association.
    if (owned instanceof HTMLTextAreaElement && this.contains(owned) &&
        owned.getAttribute('form') === this.dataset.formId && (!owned.form || owned.form === form) &&
        !controls.includes(owned)) controls.push(owned);
    return controls.filter((control) =>
      (control instanceof HTMLTextAreaElement || (control instanceof HTMLInputElement && control.type === 'text')) &&
      (!control.disabled || control === this.refs.messageInput) &&
      (control === owned || (form && control.form === form)) &&
      /^properties\[[^\]]+\]$/.test(control.name) && this.#propertyName(control.name) === expected
    );
  }

  #syncMessage(config, selected) {
    const { messageInput, messageField, messageCount } = this.refs;
    const profileEnabled = Boolean(config.messagePropertyName && Array.isArray(config.messageValues));
    if (this.#state.messageProductId !== this.dataset.productId) {
      this.#state.message = '';
      this.#state.messageInitialized = false;
      this.#state.messageProductId = this.dataset.productId;
    }
    const controls = profileEnabled ? this.#messageControls(config) : [];
    const external = controls.filter((control) => control !== messageInput);
    const control = external.includes(this.#messageControl) ? this.#messageControl : external[0] ??
      (controls.includes(messageInput) ? messageInput : null);
    if (control && control !== messageInput) {
      this.#state.message = control.value;
      this.#state.messageInitialized = true;
    } else if (control && !this.#state.messageInitialized) {
      this.#state.message = control.value;
      this.#state.messageInitialized = true;
    }
    this.#messageControl = control;
    if (messageInput) {
      messageInput.disabled = !profileEnabled || !selected || external.length > 0 || control !== messageInput;
      const limit = Number(config.messageMaxLength);
      if (Number.isInteger(limit) && limit > 0) messageInput.maxLength = limit;
      const value = this.#state.message ?? '';
      if (messageInput.value !== value) messageInput.value = value;
    }
    if (messageField) messageField.hidden = !profileEnabled || !selected || external.length > 0;
    if (messageCount && messageInput) {
      messageCount.textContent = this.#interpolate(this.dataset.messageCountTemplate, {
        count: messageInput.value.length, max: messageInput.maxLength,
      });
    }
    for (const field of controls) {
      if (this.#messageControlsBound.has(field)) continue;
      this.#messageControlsBound.add(field);
      const { signal } = this.#controller;
      field.addEventListener('input', this.#handleMessageInput, { signal });
      field.addEventListener('change', this.#handleMessageInput, { signal });
    }
    return this.#state.message ?? '';
  }

  #handleMessageInput = (event) => {
    const config = this.#configuration();
    const controls = this.#messageControls(config);
    const control = event.composedPath().find((node) => controls.includes(node));
    if (!control || (control === this.refs.messageInput && control.disabled)) return;
    this.#messageControl = control;
    this.#state.message = control.value;
    this.#state.messageInitialized = true;
    this.#state.messageProductId = this.dataset.productId;
    if (this.#messageSelected(config, this.#selectedOptions())) this.#state.view = 'back';
    this.#render();
  };

  #fitMessage() {
    const { messageBox, messageText } = this.refs;
    if (!messageBox || !messageText || messageBox.hidden) {
      this.#messageResizeObserver?.disconnect();
      this.#observedMessageBox = null;
      return;
    }
    if (typeof ResizeObserver !== 'undefined' && this.#observedMessageBox !== messageBox) {
      this.#messageResizeObserver?.disconnect();
      this.#messageResizeObserver = new ResizeObserver(() => this.#fitMessage());
      this.#messageResizeObserver.observe(messageBox);
      this.#observedMessageBox = messageBox;
    }
    if (!(messageBox.clientWidth > 0 && messageBox.clientHeight > 0)) return;
    const style = typeof getComputedStyle === 'function' ? getComputedStyle(messageBox) : null;
    const padding = (property) => Number.parseFloat(style?.[property] ?? '0') || 0;
    const width = messageBox.clientWidth - padding('paddingLeft') - padding('paddingRight');
    const height = messageBox.clientHeight - padding('paddingTop') - padding('paddingBottom');
    if (!(width > 0 && height > 0)) return;
    messageText.style.setProperty('--mh-message-scale', '1');
    let scale = 1;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (!(messageText.scrollWidth > 0 && messageText.scrollHeight > 0)) return;
      if (messageText.scrollWidth <= width && messageText.scrollHeight <= height) return;
      scale = Math.max(0.01, scale * Math.min(width / messageText.scrollWidth, height / messageText.scrollHeight) * 0.98);
      messageText.style.setProperty('--mh-message-scale', String(scale));
    }
  }

  #render() {
    this.#reconcileUploader();
    const { stage, artworkBox, artwork, status, selection, viewButtons, mockupNote, colorNote } = this.refs;
    const options = this.#selectedOptions();
    const config = this.#configuration();
    const preserveFront = config.preserveFront === true;
    const color = options.get('color') || options.get('colour') || String(config.fixedColor ?? '');
    const optionName = String(config.placementOptionName ?? 'Plus Printing').trim().toLowerCase() || 'plus printing';
    const placement = options.get(optionName) ?? '';
    const normalizedPlacement = placement.trim().toLowerCase();
    const configured = [config.frontPrintValues, config.backPrintValues, config.blankValues, config.standardFrontValues].some(Array.isArray);
    const matches = (values, fallback) => (configured ? (Array.isArray(values) ? values : []) : fallback)
      .some((value) => typeof value === 'string' && value.trim().toLowerCase() === normalizedPlacement);
    const blank = matches(config.blankValues, ['blanks', 'blank', 'blank only']);
    const frontPrint = !blank && matches(config.frontPrintValues, ['small left chest only', 'small left chest and large back']);
    const backPrint = !blank && matches(config.backPrintValues, ['large back only', 'small left chest and large back']);
    const standardFront = preserveFront && matches(config.standardFrontValues, []);
    const messageSelected = this.#messageSelected(config, options);
    const rawMessage = this.#syncMessage(config, messageSelected);
    const placementKey = `${optionName}:${normalizedPlacement}:${frontPrint}:${backPrint}:${blank}:${standardFront}:${messageSelected}`;
    if (placementKey !== this.#lastPlacement) {
      if (preserveFront) {
        if (standardFront) this.#state.view = 'front';
        else if (messageSelected || backPrint) this.#state.view = 'back';
      } else {
        if (backPrint && !frontPrint) this.#state.view = 'back';
        if (frontPrint && !backPrint) this.#state.view = 'front';
      }
      this.#lastPlacement = placementKey;
    }
    const printVisible = !messageSelected && (this.#state.view === 'front' ? !preserveFront && frontPrint : backPrint);
    const guideVisible = printVisible || (messageSelected && this.#state.view === 'back');
    if (this.refs.printArea) this.refs.printArea.hidden = !guideVisible;
    const colorValue = garmentColors[color.trim().toLowerCase()];
    this.style.setProperty('--mh-garment-color', colorValue ?? '#d6d6d6');
    stage.dataset.view = this.#state.view;
    stage.dataset.frontPlacement = this.dataset.frontPlacement === 'centre' ? 'centre' : 'chest';
    stage.dataset.colorApproximate = String(!preserveFront && (!colorValue || color.toLowerCase().includes('marle')));
    if (colorNote) colorNote.hidden = stage.dataset.colorApproximate !== 'true';
    stage.dataset.hasMockup = String(Boolean(stage.querySelector(`.merch-house-preview__mockup--${this.#state.view}`)));
    if (mockupNote) mockupNote.hidden = preserveFront || stage.dataset.hasMockup !== 'true';
    if (this.refs.messageText) this.refs.messageText.textContent = rawMessage;
    if (this.refs.messageBox) this.refs.messageBox.hidden = !messageSelected || this.#state.view !== 'back' || !rawMessage.trim();
    this.#fitMessage();
    for (const button of viewButtons ?? []) button.setAttribute('aria-pressed', String(button.dataset.view === this.#state.view));
    const activeView = (viewButtons ?? []).find((button) => button.dataset.view === this.#state.view)?.textContent.trim() ?? '';
    const displayLabels = config.placementLabels;
    const configuredLabel = displayLabels && typeof displayLabels === 'object' && Object.hasOwn(displayLabels, normalizedPlacement)
      ? displayLabels[normalizedPlacement] : null;
    const placementLabel = typeof configuredLabel === 'string' ? configuredLabel :
      messageSelected && config.messageLabel ? String(config.messageLabel) : placement;
    selection.textContent = this.#interpolate(this.dataset.selectionTemplate, { color, placement: placementLabel });
    stage.setAttribute('aria-label', this.#interpolate(this.dataset.stageTemplate, { color, placement: placementLabel, view: activeView }));

    const file = this.#state.file;
    const savedUrl = this.#state.savedUrl;
    const hasArtwork = Boolean(savedUrl || file);
    const fileName = this.#state.savedName || file?.name || this.dataset.uploadedName || '';
    if (this.#loadedFile !== file || this.#loadedUrl !== savedUrl) {
      this.#revokeUrl();
      this.#loadedFile = file;
      this.#loadedUrl = savedUrl;
      this.#previewSource = null;
      this.#imageError = false;
      artwork.removeAttribute('src');
      if (savedUrl && this.#previewable(file, savedUrl)) {
        this.#previewSource = savedUrl;
      } else if (!savedUrl && file && this.#previewable(file, null)) {
        try {
          this.#objectUrl = URL.createObjectURL(file);
          this.#previewSource = this.#objectUrl;
        } catch {
          this.#imageError = true;
        }
      }
    }
    if (this.#previewSource && artwork.getAttribute('src') !== this.#previewSource) artwork.src = this.#previewSource;
    artworkBox.hidden = !hasArtwork || !this.#previewSource || this.#imageError || !printVisible;
    let message = this.#state.needsReselect ? this.dataset.reselectMessage : this.dataset.idleMessage;
    if (hasArtwork) message = this.#interpolate(this.#imageError ? this.dataset.errorMessage
      : this.#previewSource ? this.dataset.previewMessage : this.dataset.unsupportedMessage, { file: fileName });
    if (hasArtwork && this.#previewSource && !this.#imageError && !printVisible && !blank && (frontPrint || backPrint)) {
      const otherView = (viewButtons ?? []).find((button) => button.dataset.view !== this.#state.view)?.textContent.trim() ?? '';
      message = this.#interpolate(this.dataset.otherViewMessage, { file: fileName, view: otherView });
    }
    if (blank) message = this.dataset.blankMessage;
    if (standardFront) message = this.dataset.standardFrontMessage;
    if (messageSelected) {
      message = this.#state.view === 'front' ? this.dataset.messageFrontMessage :
        rawMessage.trim() ? this.dataset.messagePreviewMessage : this.dataset.messageIdleMessage;
    }
    if (status.textContent !== message) status.textContent = message ?? '';
  }

  #interpolate(template, values) {
    return String(template ?? '').replace(/\[(\w+)\]/g, (token, key) => values[key] ?? token);
  }

  #revokeUrl() {
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
    this.#objectUrl = null;
  }
}

if (!customElements.get('merch-house-preview')) customElements.define('merch-house-preview', MerchHousePreview);
